import React from "react";
import { Tooltip } from "@mui/material";
import { mobiliTheme } from "../../../themes/mobiliTheme";

/** Attendance rollup of one appointment, for its dot in the strip. */
export type DayStatus = "attended" | "open" | "future";

/**
 * The dot colour rollup for one appointment: attended (green), in the past
 * without attendance recorded (warning — same semantics as the HelpRounded
 * icon on the card), or still ahead (quiet ink).
 */
export const appointmentDayStatus = (appointment: {
  attended?: boolean;
  start?: string | null;
}): DayStatus =>
  appointment.attended
    ? "attended"
    : appointment.start && new Date(appointment.start) < new Date()
    ? "open"
    : "future";

export interface DayCell {
  /** YYYY-MM-DD — the identity used for selection and navigation. */
  dateKey: string;
  /** The number shown in the cell. */
  dayOfMonth: number;
  /** Full label for the tooltip, e.g. "Di 28.07.2026". */
  label: string;
  /** One entry per appointment on this day. */
  appointments: DayStatus[];
}

export interface WeekRow {
  /** ISO calendar week — the row's only label, muted, on the left. */
  isoWeek: number;
  /** Mo–Fr. */
  days: DayCell[];
  /**
   * The week being viewed — banded so it stays findable. Cards appear and
   * disappear per week, so a week's vertical position is not stable between
   * patients or dates; the band is the fixed thing to read from.
   *
   * The band is `--surface-inset`, one rung DOWN the surface ladder from the
   * `--surface-sunken` card body this strip sits on ("a deeper well within a
   * sunken area"). An `--ink-band` wash was tried first and was invisible:
   * banding tokens are alphas meant for white rows, and 2.4% of navy over an
   * already-navy-tinted surface is no contrast at all.
   */
  isViewedWeek?: boolean;
}

export interface WeekContextStripProps {
  /** Three rows: the week before the viewed one, the viewed one, the next. */
  weeks: WeekRow[];
  /** YYYY-MM-DD of the calendar's selected date. */
  selectedDateKey: string;
  onSelectDate: (dateKey: string) => void;
  /**
   * Detail content for a week — its appointment cards — rendered as a
   * full-bleed row of the SAME grid, directly under that week's cells. One
   * grid is the point: the day columns stay pixel-aligned across all three
   * weeks no matter how tall the content between them is, so the fixed
   * 15-day geometry survives the interleaving. Return null for a week with
   * nothing to show and its strip row stands alone.
   */
  renderWeekContent?: (week: WeekRow) => React.ReactNode;
}

const WEEKDAY_HEADERS = ["Mo", "Di", "Mi", "Do", "Fr"];

/**
 * `future` has no inline colour: it comes from the `bg-ink-muted` class every
 * dot carries as its floor, which the literal hex values paint over. (A class,
 * not `var(--ink-text-muted)` inline — same reasoning as the status mark in
 * RxListItem.)
 */
const DOT_COLOR: Record<DayStatus, string | undefined> = {
  attended: mobiliTheme.palette.success.main,
  open: mobiliTheme.palette.warning.main,
  future: undefined,
};

/**
 * Three weeks as a mini calendar: rows are weeks, columns are weekdays, and an
 * appointment is a dot under its day number. The *position* of the marks
 * inside the fixed 3×5 grid is the information — the arrangement of the
 * patient's appointments around the viewed date is visible without reading
 * anything.
 *
 * This replaces three "Letzte/Diese/Nächste Woche" headers and the
 * "Di 28.07. – Do 30.07." empty-range text rows: a calendar grid needs no
 * heading to say which row is last week, and an empty day is simply an
 * unmarked cell. The only text left is the ISO week number, muted, where a
 * German practice expects it (KW).
 *
 * Clicking a day navigates the calendar there — the same behaviour the old
 * text rows had, but now uniformly on every day.
 *
 * With `renderWeekContent`, each week's strip row doubles as the header of its
 * detail: the cards render inside the grid, under their week's cells, so a
 * card's vertical position answers "which week?" without a label.
 */
export const WeekContextStrip: React.FC<WeekContextStripProps> = ({
  weeks,
  selectedDateKey,
  onSelectDate,
  renderWeekContent,
}) => (
  /*
    No grid gaps: the viewed week is marked with a background band, and a gap
    would cut that band into stripes. Spacing lives inside the cells instead —
    the day buttons carry their own margin, so they still read as separate
    targets while their row's background stays continuous.
  */
  <div className="grid items-center" style={{ gridTemplateColumns: "auto repeat(5, 1fr)" }}>
    <span aria-hidden />
    {WEEKDAY_HEADERS.map((label) => (
      <span key={label} className="pb-[2px] text-center text-micro font-semibold text-ink-muted">
        {label}
      </span>
    ))}

    {weeks.map((week) => (
      <React.Fragment key={week.isoWeek}>
        <span
          className={[
            // self-stretch: the grid is items-center, so this label would only
            // be as tall as its text and the band would notch at the row's
            // left edge.
            "flex items-center self-stretch py-[2px] pl-0.5 pr-1 text-micro tabular-nums text-ink-muted",
            week.isViewedWeek ? "bg-surface-inset" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          KW {week.isoWeek}
        </span>
        {week.days.map((day) => {
          const isSelected = day.dateKey === selectedDateKey;
          const hasAppointments = day.appointments.length > 0;
          const tooltip = hasAppointments
            ? `${day.label} – ${day.appointments.length} Termin${
                day.appointments.length > 1 ? "e" : ""
              }`
            : day.label;

          return (
            // The banded cell is this wrapper, not the button: a margin on the
            // button would fall outside its own background and notch the band.
            <div
              key={day.dateKey}
              className={["p-[2px]", week.isViewedWeek ? "bg-surface-inset" : ""]
                .filter(Boolean)
                .join(" ")}
            >
              <Tooltip title={tooltip}>
                <button
                  type="button"
                  aria-current={isSelected ? "date" : undefined}
                  onClick={() => onSelectDate(day.dateKey)}
                  className={[
                    "flex w-full cursor-pointer flex-col items-center justify-center rounded-control",
                    "border border-solid transition-colors duration-fast",
                    isSelected
                      ? "border-interaction bg-interaction/10"
                      : "border-transparent bg-transparent hover:bg-ink-band",
                  ].join(" ")}
                  style={{ minHeight: 26, padding: "2px 0", gap: 2 }}
                >
                <span
                  className={[
                    "text-dense tabular-nums leading-none",
                    isSelected
                      ? "font-bold text-ink-text"
                      : hasAppointments
                      ? "font-semibold text-ink-text"
                      : "text-ink-muted",
                  ].join(" ")}
                >
                  {day.dayOfMonth}
                </span>
                {/* Always-rendered dot lane, so cells with and without
                    appointments keep the same height. Three dots at most —
                    beyond that density the third says "several" well enough. */}
                <span className="flex" style={{ height: 4, gap: 2 }}>
                  {day.appointments.slice(0, 3).map((status, i) => (
                    <span
                      key={i}
                      className="rounded-pill bg-ink-muted"
                      style={{ width: 4, height: 4, backgroundColor: DOT_COLOR[status] }}
                    />
                  ))}
                </span>
                </button>
              </Tooltip>
            </div>
          );
        })}
        {(() => {
          const content = renderWeekContent?.(week);
          if (!content) return null;
          return (
            <div
              style={{ gridColumn: "1 / -1" }}
              className={[
                // Padding, not margin — the band has to cover this space too.
                "pb-1.5 pt-0.5",
                week.isViewedWeek ? "bg-surface-inset" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {content}
            </div>
          );
        })()}
      </React.Fragment>
    ))}
  </div>
);

export default WeekContextStrip;
